import { GoogleGenAI, GenerateContentResponse, FunctionDeclaration, Type } from "@google/genai";

const apiKey = process.env.API_KEY || '';
const ai = new GoogleGenAI({ apiKey });

const MODEL_ID = 'gemini-2.5-flash';

// Tool Definitions
const logWaterTool: FunctionDeclaration = {
  name: 'logWater',
  description: 'Log water intake for the user when they mention drinking water.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      amount: { type: Type.NUMBER, description: 'Amount of water in ml' },
    },
    required: ['amount'],
  },
};

const findFacilityTool: FunctionDeclaration = {
  name: 'findFacility',
  description: 'Find a gym, restaurant, or grocery store nearby.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      category: { type: Type.STRING, description: 'gym, restaurant, or grocery' },
      location: { type: Type.STRING, description: 'User location' },
    },
    required: ['category'],
  },
};

const swapMealTool: FunctionDeclaration = {
  name: 'swapMeal',
  description: 'Swap a meal in the daily plan based on preference.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      mealType: { type: Type.STRING, description: 'breakfast, lunch, or dinner' },
      preference: { type: Type.STRING, description: 'vegetarian, low-carb, lighter, etc.' },
    },
    required: ['mealType'],
  },
};

export const generateHealthResponse = async (prompt: string, context?: string): Promise<GenerateContentResponse | null> => {
  if (!apiKey) {
    console.error("API Key is missing.");
    return null;
  }

  try {
    const fullPrompt = `
      You are a specialized health assistant for the "Well Being" platform.
      Context about the user: ${context || 'General inquiry'}
      
      User Question: ${prompt}
      
      Provide a concise, helpful answer. Use a tone that is encouraging but factual.
      If the user wants to log water, find a gym, or swap a meal, use the appropriate tool.
    `;

    const response: GenerateContentResponse = await ai.models.generateContent({
      model: MODEL_ID,
      contents: fullPrompt,
      config: {
        tools: [{ functionDeclarations: [logWaterTool, findFacilityTool, swapMealTool] }],
      }
    });

    return response;
  } catch (error) {
    console.error("Gemini API Error:", error);
    return null;
  }
};

// Backwards compatibility wrapper if needed, though we will update ChatWidget to use the new function
export const generateHealthAdvice = async (prompt: string, context?: string): Promise<string> => {
    const response = await generateHealthResponse(prompt, context);
    return response?.text || "I couldn't generate a response at this time.";
};
